// "Tell me about it", the rung after two re-says (plan § 4, Edit): the
// child describes the word they want, the description is transcribed and
// sent once to the describe-word route, and what comes back is up to three
// words to hear and choose from. The description is never added to the
// composition and never kept; this component only ever sees the words it
// produced. When nothing comes back the rung says so gently and hands over
// to the grown-up, with no error language.
import { Check, Mic, Square, UserRound, Volume2 } from "lucide-react";
import { useState } from "react";

export type DescribePhase =
  | { readonly kind: "ask" }
  /** The description went nowhere (the route failed); the child may tell it again. */
  | { readonly kind: "again" }
  | { readonly kind: "listening" }
  | { readonly kind: "thinking" }
  | { readonly kind: "offer"; readonly candidates: readonly string[] }
  | { readonly kind: "nothing" };

export interface DescribeRungProps {
  readonly phase: DescribePhase;
  /** Some other recording is open or being written down; the mic waits. */
  readonly busy: boolean;
  readonly level: number;
  readonly onStart: () => void;
  readonly onStop: () => void;
  readonly onHear: (word: string) => void;
  readonly onChoose: (word: string) => void;
  readonly onNoneOfThese: () => void;
  readonly onSave: () => void;
}

const Prompt = ({ children }: { children: React.ReactNode }) => (
  <p className="m-0 text-center text-xl" style={{ color: "var(--ink-soft)" }}>
    {children}
  </p>
);

function Offer({
  candidates,
  onHear,
  onChoose,
  onNoneOfThese,
}: Pick<DescribeRungProps, "onHear" | "onChoose" | "onNoneOfThese"> & {
  candidates: readonly string[];
}) {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <div className="flex flex-col items-center gap-3">
      <Prompt>Tap a word to hear it. Is one of these your word?</Prompt>
      <div className="flex flex-wrap justify-center gap-3">
        {candidates.map((word) => (
          <button
            key={word}
            type="button"
            className="compose-btn"
            data-tone="hear"
            data-picked={picked === word ? "true" : undefined}
            onClick={() => {
              setPicked(word);
              onHear(word);
            }}
          >
            <Volume2 className="size-6" /> {word}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        <button
          type="button"
          className="compose-btn"
          data-tone="yes"
          disabled={picked === null}
          onClick={() => picked !== null && onChoose(picked)}
        >
          <Check className="size-7" /> That one
        </button>
        <button type="button" className="compose-btn" onClick={onNoneOfThese}>
          None of these
        </button>
      </div>
    </div>
  );
}

export function DescribeRung(props: DescribeRungProps) {
  const { phase } = props;
  switch (phase.kind) {
    case "ask":
    case "again":
    case "listening":
    case "thinking":
      return (
        <div className="flex flex-col items-center gap-3">
          <Prompt>
            {phase.kind === "ask"
              ? "Tell me about that word — what is it like?"
              : phase.kind === "again"
                ? "Hmm, I missed that. Tell me about it one more time?"
                : phase.kind === "listening"
                  ? "I’m listening — tap when you’re done"
                  : "Thinking about your word…"}
          </Prompt>
          <button
            type="button"
            className="compose-btn"
            data-tone="talk"
            disabled={
              phase.kind === "thinking" ||
              (props.busy && phase.kind !== "listening")
            }
            style={{ "--level": props.level } as React.CSSProperties}
            onClick={phase.kind === "listening" ? props.onStop : props.onStart}
          >
            {phase.kind === "listening" ? (
              <Square className="size-7" />
            ) : (
              <Mic className="size-7" />
            )}
            {phase.kind === "listening" ? "Done" : "Tell me about it"}
          </button>
        </div>
      );
    case "offer":
      return (
        <Offer
          candidates={phase.candidates}
          onHear={props.onHear}
          onChoose={props.onChoose}
          onNoneOfThese={props.onNoneOfThese}
        />
      );
    case "nothing":
      return (
        <div className="flex flex-col items-center gap-3">
          <Prompt>
            That one is a puzzle. Let&rsquo;s save it for a grown-up.
          </Prompt>
          <button
            type="button"
            className="compose-btn"
            data-tone="grownup"
            onClick={props.onSave}
          >
            <UserRound className="size-6" /> Save it for a grown-up
          </button>
        </div>
      );
  }
}
